/**
 * Base64 without the browser.
 *
 * `btoa` and `atob` are part of the web platform, not of JavaScript. React
 * Native has neither, so a package that claims to be reusable by a native
 * client cannot reach for them — and both places that did are on the critical
 * path: encoding a reply marker into a message body, and putting the access
 * token into the WebSocket subprotocol. A native client would have failed at
 * the first message it sent and at the moment it opened its socket.
 *
 * The implementation is deliberately small and dependency-free rather than a
 * polyfill installed globally: nothing here should depend on which globals the
 * host happens to provide.
 */

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

const LOOKUP = /* @__PURE__ */ (() => {
  const table = new Uint8Array(128).fill(255);
  for (let index = 0; index < ALPHABET.length; index += 1) {
    table[ALPHABET.charCodeAt(index)] = index;
  }
  return table;
})();

/** Encode bytes as standard base64, with padding. */
export function encodeBase64(bytes: Uint8Array): string {
  let output = '';
  let index = 0;

  for (; index + 2 < bytes.length; index += 3) {
    const triple = (bytes[index]! << 16) | (bytes[index + 1]! << 8) | bytes[index + 2]!;
    output +=
      ALPHABET[(triple >> 18) & 63]! +
      ALPHABET[(triple >> 12) & 63]! +
      ALPHABET[(triple >> 6) & 63]! +
      ALPHABET[triple & 63]!;
  }

  const remaining = bytes.length - index;
  if (remaining === 1) {
    const chunk = bytes[index]! << 16;
    output += `${ALPHABET[(chunk >> 18) & 63]!}${ALPHABET[(chunk >> 12) & 63]!}==`;
  } else if (remaining === 2) {
    const chunk = (bytes[index]! << 16) | (bytes[index + 1]! << 8);
    output +=
      `${ALPHABET[(chunk >> 18) & 63]!}${ALPHABET[(chunk >> 12) & 63]!}` +
      `${ALPHABET[(chunk >> 6) & 63]!}=`;
  }
  return output;
}

/**
 * Decode standard base64 into bytes.
 *
 * Throws on input that is not base64 rather than guessing: the values decoded
 * here arrive inside message bodies, and quietly returning rubbish would put
 * that rubbish on somebody's screen.
 */
export function decodeBase64(value: string): Uint8Array {
  const trimmed = value.replace(/=+$/, '');
  const output = new Uint8Array((trimmed.length * 3) >> 2);
  let accumulator = 0;
  let bits = 0;
  let written = 0;

  for (let index = 0; index < trimmed.length; index += 1) {
    const code = trimmed.charCodeAt(index);
    const digit = code < 128 ? LOOKUP[code]! : 255;
    if (digit === 255) throw new Error('Строка не является base64.');
    accumulator = (accumulator << 6) | digit;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      output[written] = (accumulator >> bits) & 0xff;
      written += 1;
    }
  }
  return output.subarray(0, written);
}

/** Encode ASCII text as base64url without padding, for use as an HTTP token. */
export function encodeBase64Url(text: string): string {
  const bytes = new Uint8Array(text.length);
  for (let index = 0; index < text.length; index += 1) {
    bytes[index] = text.charCodeAt(index) & 0xff;
  }
  return encodeBase64(bytes).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}
